import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 4: NEELA
 * Style: Ocean Fresh — Sophisticated Indian Fashion
 * Deep navy accents with clean white and light teal highlights
 */
const Template4 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);

    // Categories
    const categories = [
        {
            name: 'Silk Collection',
            description: 'Handwoven silk sarees and ensembles crafted by master weavers',
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Bridal Wear',
            description: 'Exquisite bridal lehengas and wedding attire for your special day',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Everyday Elegance',
            description: 'Contemporary kurtas and suits for the modern Indian woman',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Handloom Specials',
            description: 'Authentic handloom textiles celebrating India\'s rich weaving heritage',
            image: 'https://images.pexels.com/photos/2118563/pexels-photo-2118563.jpeg?auto=compress&cs=tinysrgb&w=600'
        }
    ];

    // Products
    const products = [
        { id: 1, name: 'Banarasi Silk Saree', price: '\u20B912,999', tag: 'NEW', image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Designer Lehenga Set', price: '\u20B924,500', originalPrice: '\u20B929,999', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Cotton Kurta Collection', price: '\u20B93,499', tag: 'NEW', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Chanderi Silk Dupatta', price: '\u20B94,999', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Embroidered Anarkali', price: '\u20B98,999', originalPrice: '\u20B911,999', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Tussar Silk Saree', price: '\u20B915,999', tag: 'NEW', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Bridal Lehenga', price: '\u20B945,000', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Zardozi Work Kurta', price: '\u20B96,499', originalPrice: '\u20B98,999', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' }
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The silk saree I ordered is absolutely stunning. The craftsmanship and attention to detail is remarkable. I received so many compliments at the wedding!',
            name: 'Ananya Sharma',
            city: 'Mumbai',
            rating: 5
        },
        {
            text: 'My bridal lehenga was everything I dreamed of and more. The embroidery work is exquisite. Thank you for making my special day even more beautiful.',
            name: 'Meera Patel',
            city: 'Ahmedabad',
            rating: 5
        },
        {
            text: 'I love the cotton kurta collection. Perfect for everyday wear yet so elegant. The fabric quality is exceptional and the fit is perfect.',
            name: 'Kavitha Reddy',
            city: 'Hyderabad',
            rating: 5
        }
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen bg-white">
            {/* Google Fonts */}
            <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            {/* ===== SECTION 1: IMAGE HERO WITH NAVY OVERLAY ===== */}
            <section className="relative w-full min-h-[90vh] md:min-h-screen overflow-hidden flex items-center justify-center">
                {/* Background Image */}
                <img
                    src="https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=1200"
                    alt="Indian Fashion"
                    className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Navy Overlay */}
                <div className="absolute inset-0" style={{ backgroundColor: 'rgba(27, 73, 101, 0.65)' }} />

                {/* Hero Content */}
                <div className="relative z-10 text-center px-4 sm:px-6 max-w-3xl mx-auto">
                    <p className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-4 sm:mb-6 font-medium text-[#62B6CB]">
                        Curated Indian Fashion
                    </p>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold mb-4 sm:mb-6 leading-tight tracking-wide text-white">
                        Elegance in Every Thread
                    </h1>
                    <p className="text-sm sm:text-base md:text-lg font-light mb-8 sm:mb-10 max-w-lg mx-auto text-white/80">
                        Discover handcrafted Indian fashion that celebrates tradition with a modern touch
                    </p>
                    <button
                        onClick={() => navigate('/template4/shop')}
                        className="inline-flex items-center gap-2 px-8 py-3 sm:px-10 sm:py-3.5 text-sm font-medium tracking-wider uppercase bg-white text-[#1B4965] hover:bg-[#F0F7FA] transition-all duration-300 rounded-sm"
                    >
                        Shop Now <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                    <div className="w-px h-10 bg-white/30">
                        <div className="w-full h-1/2 bg-white/70" style={{ animation: 'neelaPulse 2s ease-in-out infinite' }} />
                    </div>
                </div>
                <style>{`
                    @keyframes neelaPulse {
                        0%, 100% { opacity: 0.3; transform: translateY(0); }
                        50% { opacity: 1; transform: translateY(10px); }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 2: CATEGORIES ===== */}
            <section className="py-12 md:py-20 lg:py-24 bg-[#F0F7FA]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 md:mb-14">
                        <p className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-2 text-[#62B6CB] font-medium">
                            Browse
                        </p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1B4965]">
                            Our Collections
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template4/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group rounded-sm"
                            >
                                <img
                                    src={category.image}
                                    alt={category.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{ background: 'linear-gradient(to top, rgba(27,73,101,0.85) 0%, rgba(27,73,101,0.2) 50%, transparent 100%)' }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                                    <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white mb-1">
                                        {category.name}
                                    </h3>
                                    <p className="text-xs text-white/70 hidden md:block leading-relaxed">
                                        {category.description}
                                    </p>
                                    <span className="text-xs tracking-wider uppercase inline-flex items-center gap-1 mt-2 text-[#62B6CB] font-medium">
                                        Explore <ArrowRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: PRODUCTS GRID ===== */}
            <section className="py-12 md:py-20 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 md:mb-14">
                        <p className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-2 text-[#62B6CB] font-medium">
                            Featured
                        </p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1B4965]">
                            New Arrivals
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template4/shop')}
                                className="cursor-pointer group bg-white border border-gray-100 hover:border-[#1B4965]/20 transition-all duration-300 rounded-sm overflow-hidden"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    {product.tag && (
                                        <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] sm:text-xs font-semibold tracking-wider uppercase bg-[#1B4965] text-white rounded-sm">
                                            {product.tag}
                                        </span>
                                    )}
                                    {product.originalPrice && !product.tag && (
                                        <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] sm:text-xs font-semibold tracking-wider uppercase bg-[#62B6CB] text-white rounded-sm">
                                            SALE
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center bg-white/80 hover:bg-white transition-colors"
                                    >
                                        <Heart
                                            className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#1B4965' : '#6B7280',
                                                fill: wishlist[product.id] ? '#1B4965' : 'none'
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-3 sm:p-4">
                                    <h3 className="text-xs sm:text-sm font-medium text-[#1A1A1A] line-clamp-1 mb-1">
                                        {product.name}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <p className="text-xs sm:text-sm font-semibold text-[#1B4965]">{product.price}</p>
                                        {product.originalPrice && (
                                            <p className="text-xs text-[#6B7280] line-through">{product.originalPrice}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-8 md:mt-12">
                        <button
                            onClick={() => navigate('/template4/shop')}
                            className="inline-flex items-center gap-2 px-8 py-3 text-sm font-medium tracking-wider uppercase border border-[#1B4965] text-[#1B4965] hover:bg-[#1B4965] hover:text-white transition-all duration-300 rounded-sm"
                        >
                            View All Products <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: CUSTOMER STORIES / TESTIMONIALS ===== */}
            <section className="py-12 md:py-20 lg:py-24 bg-[#F0F7FA]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 md:mb-14">
                        <p className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-2 text-[#62B6CB] font-medium">
                            Testimonials
                        </p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1B4965]">
                            Customer Stories
                        </h2>
                    </div>

                    <div
                        className="relative"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="bg-white rounded-sm p-6 sm:p-8 md:p-12 text-center shadow-sm border border-gray-100">
                            <div className="flex justify-center gap-1 mb-4 sm:mb-6">
                                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-[#62B6CB] text-[#62B6CB]" />
                                ))}
                            </div>
                            <p className="text-sm sm:text-base md:text-lg leading-relaxed text-[#1A1A1A] mb-6 sm:mb-8 max-w-2xl mx-auto font-light italic">
                                "{testimonials[activeTestimonial].text}"
                            </p>
                            <p className="text-sm sm:text-base font-semibold text-[#1B4965]">
                                {testimonials[activeTestimonial].name}
                            </p>
                            <p className="text-xs sm:text-sm text-[#6B7280]">
                                {testimonials[activeTestimonial].city}
                            </p>
                        </div>

                        {/* Dots */}
                        <div className="flex justify-center gap-2 mt-6">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                        index === activeTestimonial ? 'bg-[#1B4965] w-6' : 'bg-[#1B4965]/30 w-2'
                                    }`}
                                />
                            ))}
                        </div>

                        {/* Nav Arrows (Desktop) */}
                        <button
                            onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 w-10 h-10 items-center justify-center rounded-full border border-[#1B4965]/20 text-[#1B4965] hover:bg-[#1B4965] hover:text-white transition-all"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
                            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 w-10 h-10 items-center justify-center rounded-full border border-[#1B4965]/20 text-[#1B4965] hover:bg-[#1B4965] hover:text-white transition-all"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: NEWSLETTER ===== */}
            <section className="py-12 md:py-20 lg:py-24 bg-[#1B4965]">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-3 sm:mb-4">
                        Stay in the Loop
                    </h2>
                    <p className="text-sm sm:text-base text-white/70 mb-6 sm:mb-8 font-light">
                        Subscribe to get updates on new collections, exclusive offers, and styling tips
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 px-4 py-3 text-sm bg-white/10 border border-white/20 text-white placeholder-white/50 rounded-sm focus:outline-none focus:border-[#62B6CB]"
                        />
                        <button className="px-6 py-3 text-sm font-medium tracking-wider uppercase bg-white text-[#1B4965] hover:bg-[#F0F7FA] transition-all duration-300 rounded-sm">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template4;
